import type { SummaryResponse, SummaryStatus, EncryptedPayload, PersonSummary } from "~/types/global";
import { SummaryModel } from "root/domain/entities/summary.model";
import { isPersonSummary, isNestedSummaryBySection } from "~/helpers/summary-type-guards.helper";

/**
 * DTO for handling Summary API responses
 * Validates and transforms the raw API response into a SummaryModel
 */
export class SummaryOutputDTO {
  private readonly summaryModel: SummaryModel;

  constructor(data: unknown) {
    const validated = this.validateAndTransform(data);
    this.summaryModel = new SummaryModel(validated);
  }

  private validateAndTransform(data: unknown): SummaryResponse {
    if (!data || typeof data !== "object") {
      console.error("Invalid data format for summary: expected an object", data);
      throw new Error("Erro ao buscar sumário executivo.");
    }

    const response = data as Record<string, any>;
    console.log("Raw summary response:", response);

    // Validate required fields
    if (!this.isValidSummaryResponse(response)) {
      console.error("Invalid summary response structure", response);
      throw new Error("Erro ao buscar sumário executivo.");
    }

    // Filtra tópicos inválidos ao invés de rejeitar toda a resposta
    if (response.data && typeof response.data === "object" && !this.isValidEncryptedPayload(response.data)) {
      response.data = this.sanitizeSummaryData(response.data);
    }

    return response as SummaryResponse;
  }

  private isValidSummaryResponse(data: any): data is SummaryResponse {
    const requiredFields = [
      'summary_id',
      'report_id',
      'summary_status'
    ] as const;

    // Check if all required fields exist
    if (!requiredFields.every(field => field in data)) {
      console.error("Missing required fields in summary response");
      return false;
    }

    // Validate field types - summary_id can be string or null
    if (data.summary_id !== null && typeof data.summary_id !== "string") {
      console.error("Invalid summary_id type");
      return false;
    }

    if (typeof data.report_id !== "string") {
      console.error("Invalid report_id type");
      return false;
    }

    // Validate status - can be null or one of the valid statuses
    const validStatuses: SummaryStatus[] = ["pending", "success", "error", null];
    if (!validStatuses.includes(data.summary_status)) {
      console.error("Invalid summary_status value");
      return false;
    }

    // Validate report_name (can be string or encrypted)
    if (data.report_name !== undefined && data.report_name !== null) {
      const isValidName = typeof data.report_name === "string" ||
        this.isValidEncryptedPayload(data.report_name);
      if (!isValidName) {
        console.error("Invalid report_name type");
        return false;
      }
    }

    // Validate data field if present
    if (data.data !== null && data.data !== undefined) {
      const isValidData = this.hasValidStructure(data.data) ||
        this.isValidEncryptedPayload(data.data);
      if (!isValidData) {
        console.error("Invalid data structure");
        return false;
      }
    }

    return true;
  }

  /**
   * Verifica se os dados têm a estrutura básica correta (sem validar cada tópico)
   */
  private hasValidStructure(data: any): boolean {
    if (!data || typeof data !== "object") return false;
    if (!("topics" in data) || !Array.isArray(data.topics)) return false;
    return true;
  }

  /**
   * Sanitiza os dados removendo tópicos inválidos e expandindo tópicos
   * com summary_by_section aninhado (Múltiplos Registros) em N+1 tópicos planos.
   */
  private sanitizeSummaryData(data: any): any {
    if (!data || typeof data !== "object") return data;
    if (!Array.isArray(data.topics)) return data;

    const expandedTopics: any[] = [];

    for (const topic of data.topics) {
      const isValid = this.isValidTopic(topic);
      if (!isValid) {
        if (topic?.title) {
          console.warn(`Topic "${topic.title}" has invalid format and will be filtered out`);
        }
        continue;
      }

      // Check if this topic has nested summary_by_section (Múltiplos Registros)
      if (
        topic.summary_by_section &&
        typeof topic.summary_by_section === "object" &&
        isNestedSummaryBySection(topic.summary_by_section)
      ) {
        const nestedTopics = this.expandNestedTopic(topic);
        expandedTopics.push(...nestedTopics);
      } else {
        expandedTopics.push(topic);
      }
    }

    return {
      ...data,
      topics: expandedTopics
    };
  }

  /**
   * Expande um tópico com summary_by_section aninhado (Record<string, PersonSummary>)
   * em N+1 tópicos planos:
   * - 1 tópico pai: person_name → executive_summary_text
   * - N tópicos filhos: um por pessoa, com summary_by_section mesclado de todos os inner topics
   */
  private expandNestedTopic(topic: any): any[] {
    const nestedSummary = topic.summary_by_section as Record<string, PersonSummary>;
    const parentTopicId = topic.id || "t-Múltiplos Registros Encontrados";
    const results: any[] = [];

    // Build parent topic: person_name → executive_summary_text (flat Record<string, string>)
    const parentSummaryBySection: Record<string, string> = {};
    for (const [personName, personSummary] of Object.entries(nestedSummary)) {
      parentSummaryBySection[personName] = personSummary.executive_summary || "";
    }

    const parentTopic = {
      ...topic,
      id: "t-Múltiplos Registros Encontrados",
      title: topic.title || "Múltiplos Registros Encontrados",
      related_sections: topic.related_sections || ["Múltiplos Registros Encontrados"],
      summary_by_section: parentSummaryBySection,
      _nested_summary_by_section: nestedSummary,
    };
    results.push(parentTopic);

    // Build N child topics: one per person
    const personEntries = Object.entries(nestedSummary);
    personEntries.forEach(([personName, personSummary], index) => {
      // Merge all inner topics' summary_by_section into a single flat Record<string, string>
      const mergedSummaryBySection: Record<string, string> = {};
      if (Array.isArray(personSummary.topics)) {
        for (const innerTopic of personSummary.topics) {
          if (innerTopic.summary_by_section && typeof innerTopic.summary_by_section === "object") {
            for (const [sectionName, sectionSummary] of Object.entries(innerTopic.summary_by_section)) {
              if (typeof sectionSummary === "string") {
                mergedSummaryBySection[sectionName] = sectionSummary;
              }
            }
          }
        }
      }

      const childTopic = {
        id: `t-multiplos-person-${index}`,
        title: personName,
        related_sections: topic.related_sections || ["Múltiplos Registros Encontrados"],
        summary_by_section: mergedSummaryBySection,
        tags: topic.tags || [],
        entities: topic.entities || [],
        _parentTopicId: parentTopicId,
      };
      results.push(childTopic);
    });

    return results;
  }

  private isValidTopic(topic: any): boolean {
    if (!topic || typeof topic !== "object") return false;

    if (typeof topic.id !== "string") return false;
    if (typeof topic.title !== "string") return false;
    if (!Array.isArray(topic.related_sections)) return false;

    // Valida summary_by_section se presente
    if (topic.summary_by_section !== undefined && topic.summary_by_section !== null) {
      if (!this.isValidSummaryBySection(topic.summary_by_section)) {
        console.warn(`Topic "${topic.title}" has invalid summary_by_section format, will be filtered out`);
        return false;
      }
    }

    return true;
  }

  /**
   * Valida se summary_by_section está no formato correto:
   * - Flat: Record<string, string> (todos os valores são strings não vazias)
   * - Nested: Record<string, PersonSummary> (todos os valores são objetos com executive_summary e topics)
   * Formatos mistos (alguns string, alguns objeto) são rejeitados.
   */
  private isValidSummaryBySection(value: unknown): value is Record<string, string> | Record<string, PersonSummary> {
    if (!value || typeof value !== 'object') {
      return false;
    }

    const entries = Object.entries(value);

    // Deve ter pelo menos uma entrada
    if (entries.length === 0) {
      return false;
    }

    // Verifica se todos os valores são strings (flat format)
    const allStrings = entries.every(([key, val]) =>
      typeof key === 'string' &&
      typeof val === 'string' &&
      val.trim().length > 0
    );

    if (allStrings) {
      return true;
    }

    // Verifica se todos os valores são PersonSummary (nested format)
    const allNested = entries.every(([key, val]) =>
      typeof key === 'string' &&
      isPersonSummary(val)
    );

    if (allNested) {
      return true;
    }

    // Formato misto — rejeitado
    return false;
  }

  private isValidEncryptedPayload(data: any): data is EncryptedPayload {
    return (
      data != null &&
      typeof data === "object" &&
      "encrypted" in data &&
      "iv" in data &&
      typeof data.encrypted === "string" &&
      typeof data.iv === "string"
    );
  }

  /**
   * Get the summary model
   */
  public getSummaryModel(): SummaryModel {
    return this.summaryModel;
  }

  /**
   * Get the raw summary response
   */
  public getRaw(): SummaryResponse {
    return this.summaryModel.raw;
  }

  /**
   * Get the summary ID
   */
  public getSummaryId(): string {
    return this.summaryModel.summaryId;
  }

  /**
   * Get the report ID
   */
  public getReportId(): string {
    return this.summaryModel.reportId;
  }

  /**
   * Get the summary status
   */
  public getStatus(): SummaryStatus {
    return this.summaryModel.summaryStatus;
  }

  /**
   * Check if the summary is successfully generated
   */
  public isSuccess(): boolean {
    return this.summaryModel.isSuccess;
  }

  /**
   * Check if the summary is pending
   */
  public isPending(): boolean {
    return this.summaryModel.isPending;
  }

  /**
   * Check if the summary has an error
   */
  public isError(): boolean {
    return this.summaryModel.isError;
  }

  /**
   * Check if the summary has data
   */
  public hasData(): boolean {
    return this.summaryModel.hasData;
  }

  /**
   * Get the number of topics
   */
  public getTopicsCount(): number {
    return this.summaryModel.topicsCount;
  }

  /**
   * Get normalized summary data
   */
  public toJSON() {
    return this.summaryModel.toJSON();
  }
}
